import { Building2, ChevronRight, Target } from 'lucide-react'
import { Link, useLocation, useParams } from '@tanstack/react-router'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/layout/sidebar'
import { CornerBottomIcon } from '@/components/ui/corner-bottom-icon'
import { isOgsmEnabled } from '@/configs/feature-flags'
import { OGSM_CAPABILITY } from '@/features/ogsm/access'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { useCompany } from '@/hooks/use-companies'
import { cn } from '@/lib/utils'
import { selectCapabilities, useUserStore } from '@/stores/user.store'

const SUB_BUTTON_CLASS =
  'rounded-sm text-primary/70 hover:text-secondary hover:bg-secondary/10 group/subitem'

/**
 * Company-scoped navigation, shown while a company is open. Today it holds
 * OGSM; the company's own page stays reachable from it. Hidden unless OGSM is
 * on for this build and the user is a Greybull user, so it adds nothing for
 * everyone else.
 */
export function OpenCompanyNav() {
  const { companyId } = useParams({ strict: false })
  const { pathname } = useLocation()
  const { state } = useSidebar()
  const capabilities = useUserStore(selectCapabilities)
  const enabled = isOgsmEnabled() && capabilities.includes(OGSM_CAPABILITY)
  const { data: company } = useCompany(enabled && companyId ? companyId : null)

  if (!enabled || !companyId) return null

  const ogsmActive = pathname.endsWith('/ogsm')
  const overviewActive = !ogsmActive

  if (state === 'collapsed') {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={OGSM_COPY.title}
                className={cn(
                  'rounded-sm text-primary/70 hover:!text-secondary hover:!bg-secondary/10',
                  ogsmActive && '!text-secondary !bg-secondary/10'
                )}
              >
                <Link to="/companies/$companyId/ogsm" params={{ companyId }}>
                  <Target />
                  <span>{OGSM_COPY.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    )
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-[11px] font-medium uppercase tracking-wide text-primary/70">
        Open company
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          <Collapsible asChild defaultOpen className="group/collapsible">
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  tooltip={company?.displayName}
                  className="rounded-sm text-primary/70 hover:!text-secondary hover:!bg-secondary/10 group/parent cursor-pointer"
                >
                  <Building2 className="text-primary/70 group-hover/parent:text-secondary transition-colors" />
                  <span className="truncate">
                    {company?.displayName ?? '…'}
                  </span>
                  <ChevronRight className="ml-auto text-primary/70 group-hover/parent:text-secondary transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      className={cn(
                        SUB_BUTTON_CLASS,
                        overviewActive && 'text-secondary bg-secondary/10'
                      )}
                    >
                      <Link to="/companies/$companyId" params={{ companyId }}>
                        <CornerBottomIcon className="size-4 text-primary group-hover/subitem:text-secondary" />
                        <span className="truncate">Overview</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      className={cn(
                        SUB_BUTTON_CLASS,
                        ogsmActive && 'text-secondary bg-secondary/10'
                      )}
                    >
                      <Link
                        to="/companies/$companyId/ogsm"
                        params={{ companyId }}
                      >
                        <CornerBottomIcon className="size-4 text-primary group-hover/subitem:text-secondary" />
                        <span className="truncate">{OGSM_COPY.title}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
